import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  MenuItem,
  FormControl,
  Select,
  TextField,
  Typography,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import dayjs, { type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export interface NewRowPayload {
  date: string;
  shift: string;
  equipment: string;
  status: string;
  reason: string;
  location: string;
  region: string;
  comment: string;
  timeCategory: string;
  timeStart: string; // ISO UTC
  timeEnd: string; // ISO UTC
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: NewRowPayload) => Promise<void>;
  shiftOptions: string[];
  equipmentOptions: string[];
  statusOptions: string[];
  statusReasonMap: Record<string, string[]>;
  statusTimeCategoryMap: Record<string, string[]>;
  locationOptions: string[];
  inputOutlineSx: object;
}

const AddEquipmentStatusRowModal = ({
  open,
  onClose,
  onSubmit,
  shiftOptions,
  equipmentOptions,
  statusOptions,
  statusReasonMap,
  statusTimeCategoryMap,
  locationOptions,
  inputOutlineSx,
}: Props) => {
  const [date, setDate] = useState<Dayjs | null>(dayjs());
  const [shift, setShift] = useState<string>('');
  const [equipment, setEquipment] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [reason, setReason] = useState<string | null>(null);
  const [location, setLocation] = useState<string | null>(null);
  const [region, setRegion] = useState<string>('');
  const [comment, setComment] = useState<string>('');
  const [timeCategory, setTimeCategory] = useState<string | null>(null);
  const [timeStart, setTimeStart] = useState<Dayjs | null>(null);
  const [timeEnd, setTimeEnd] = useState<Dayjs | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState(false);

  const reasonOptions = status
    ? statusReasonMap[status.toLowerCase()] ?? []
    : [];
  const timeCategoryOptions = status
    ? statusTimeCategoryMap[status.toLowerCase()] ?? []
    : [];

  const reset = () => {
    setDate(dayjs());
    setShift('');
    setEquipment(null);
    setStatus(null);
    setReason(null);
    setLocation(null);
    setRegion('');
    setComment('');
    setTimeCategory(null);
    setTimeStart(null);
    setTimeEnd(null);
    setTouched(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  // All fields required; end must not be before start
  const timeOrderInvalid = Boolean(
    timeStart && timeEnd && timeEnd.isBefore(timeStart)
  );
  const isValid =
    Boolean(date) &&
    Boolean(shift) &&
    Boolean(equipment) &&
    Boolean(status) &&
    Boolean(reason) &&
    Boolean(location) &&
    region.trim().length > 0 &&
    Boolean(timeCategory) &&
    Boolean(timeStart) &&
    Boolean(timeEnd) &&
    !timeOrderInvalid;

  const handleSubmit = async () => {
    setTouched(true);
    if (!isValid || !date || !timeStart || !timeEnd) return;
    setSubmitting(true);
    try {
      await onSubmit({
        date: date.format('YYYY-MM-DD'),
        shift,
        equipment: equipment ?? '',
        status: status ?? '',
        reason: reason ?? '',
        location: location ?? '',
        region: region.trim(),
        comment: comment.trim(),
        timeCategory: timeCategory ?? '',
        timeStart: timeStart.utc().toISOString(),
        timeEnd: timeEnd.utc().toISOString(),
      });
      reset();
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const labelSx = {
    fontWeight: 600,
    color: 'text.primary',
    mb: 0.75,
  } as const;

  const showErr = (v: boolean) => touched && v;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Add Row</DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
          }}
        >
          {/* Date */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Date *
            </Typography>
            <DatePicker
              value={date}
              maxDate={dayjs()}
              onChange={(v) => {
                setDate(v);
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  error: showErr(!date),
                  sx: inputOutlineSx,
                },
              }}
            />
          </Box>

          {/* Shift */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Shift *
            </Typography>
            <FormControl size="small" fullWidth sx={inputOutlineSx}>
              <Select
                value={shift}
                displayEmpty
                error={showErr(!shift)}
                onChange={(e) => {
                  setShift(e.target.value);
                }}
                renderValue={(v) =>
                  v || <span style={{ color: '#aaa' }}>Select shift</span>
                }
              >
                {shiftOptions.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Equipment */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Equipment *
            </Typography>
            <Autocomplete
              size="small"
              options={equipmentOptions}
              value={equipment}
              onChange={(_, v) => {
                setEquipment(v);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select equipment"
                  error={showErr(!equipment)}
                  sx={inputOutlineSx}
                />
              )}
            />
          </Box>

          {/* Status */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Status *
            </Typography>
            <Autocomplete
              size="small"
              options={statusOptions}
              value={status}
              onChange={(_, v) => {
                setStatus(v);
                // Reason & time category depend on status
                setReason(null);
                setTimeCategory(null);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select status"
                  error={showErr(!status)}
                  sx={inputOutlineSx}
                />
              )}
            />
          </Box>

          {/* Reason */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Reason *
            </Typography>
            <Autocomplete
              size="small"
              disabled={!status}
              options={reasonOptions}
              value={reason}
              onChange={(_, v) => {
                setReason(v);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={status ? 'Select reason' : 'Select status first'}
                  error={showErr(!reason)}
                  sx={inputOutlineSx}
                />
              )}
            />
          </Box>

          {/* Time Category */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Time Category *
            </Typography>
            <Autocomplete
              size="small"
              disabled={!status}
              options={timeCategoryOptions}
              value={timeCategory}
              onChange={(_, v) => {
                setTimeCategory(v);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={
                    status ? 'Select time category' : 'Select status first'
                  }
                  error={showErr(!timeCategory)}
                  sx={inputOutlineSx}
                />
              )}
            />
          </Box>

          {/* Location */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Location *
            </Typography>
            <Autocomplete
              size="small"
              options={locationOptions}
              value={location}
              onChange={(_, v) => {
                setLocation(v);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select location"
                  error={showErr(!location)}
                  sx={inputOutlineSx}
                />
              )}
            />
          </Box>

          {/* Region */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Region *
            </Typography>
            <TextField
              size="small"
              fullWidth
              value={region}
              placeholder="Enter region"
              error={showErr(region.trim().length === 0)}
              onChange={(e) => {
                setRegion(e.target.value);
              }}
              sx={inputOutlineSx}
            />
          </Box>

          {/* Time Start */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Time *
            </Typography>
            <DateTimePicker
              value={timeStart}
              ampm={false}
              format="DD/MM/YY HH:mm"
              maxDateTime={timeEnd ?? undefined}
              timeSteps={{ hours: 1, minutes: 1 }}
              onChange={(v) => {
                setTimeStart(v);
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  error: showErr(!timeStart),
                  sx: inputOutlineSx,
                },
              }}
            />
          </Box>

          {/* Time End */}
          <Box>
            <Typography variant="body2" sx={labelSx}>
              Time End *
            </Typography>
            <DateTimePicker
              value={timeEnd}
              ampm={false}
              format="DD/MM/YY HH:mm"
              minDateTime={timeStart ?? undefined}
              timeSteps={{ hours: 1, minutes: 1 }}
              onChange={(v) => {
                setTimeEnd(v);
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  error: showErr(!timeEnd || timeOrderInvalid),
                  helperText: timeOrderInvalid
                    ? 'Time End must be after Time'
                    : undefined,
                  sx: inputOutlineSx,
                },
              }}
            />
          </Box>

          {/* Comment — full width */}
          <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
            <Typography variant="body2" sx={labelSx}>
              Comment
            </Typography>
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={comment}
              placeholder="Enter comment"
              onChange={(e) => {
                setComment(e.target.value);
              }}
              sx={inputOutlineSx}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          disabled={submitting}
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            void handleSubmit();
          }}
          disabled={submitting || (touched && !isValid)}
          sx={{ textTransform: 'none' }}
        >
          {submitting ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            'Add Row'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEquipmentStatusRowModal;
